import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithErrorHandling } from "../baseQuery";

export const worksApi = createApi({
  reducerPath: "worksApi",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ["Works"],
  refetchOnFocus: true,
  refetchOnReconnect: true,

  endpoints: (builder) => ({
    getWorks: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        sortBy = "id",
        order = "asc",
      } = {}) => ({
        url: "/works",
        params: { page, limit, search, sortBy, order },
      }),
      transformResponse: (response) => ({
        works: response?.data ?? [],
        total: response?.total ?? 0,
        page: response?.page ?? 1,
        limit: response?.limit ?? 10,
      }),
      providesTags: (result) =>
        result?.works?.length
          ? [
              ...result.works.map(({ id }) => ({
                type: "Works",
                id: Number(id),
              })),
              { type: "Works", id: "LIST" },
            ]
          : [{ type: "Works", id: "LIST" }],
      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: true,
    }),

    getWorkById: builder.query({
      query: (id) => `/works/${id}`,
      transformResponse: (response) =>
        response?.data ? response.data : response,
      providesTags: (result, error, id) => [{ type: "Works", id }],
    }),

    getWorkBySlug: builder.query({
      query: (slug) => `/works/slug/${slug}`,
      transformResponse: (response) =>
        response?.data ? response.data : response,
      providesTags: (result, error, slug) => [{ type: "Works", id: slug }],
    }),

    createWork: builder.mutation({
      query: (data) => ({
        url: "/works",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Works", id: "LIST" }],
    }),

    updateWork: builder.mutation({
      query: ({ id, data }) => ({
        url: `/works/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Works", id },
        { type: "Works", id: "LIST" },
      ],
    }),

    deleteWork: builder.mutation({
      query: (id) => ({
        url: `/works/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Works", id: "LIST" }],
    }),

    toggleWorkStatus: builder.mutation({
      query: (id) => ({
        url: `/works/${id}/toggle`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Works", id },
        { type: "Works", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetWorksQuery,
  useGetWorkByIdQuery,
  useGetWorkBySlugQuery,
  useCreateWorkMutation,
  useUpdateWorkMutation,
  useDeleteWorkMutation,
  useToggleWorkStatusMutation,
} = worksApi;
